// @ts-nocheck
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface AccessibleArea {
  label: string;
  path: string;
}

const ADMIN_ROLES = [
  'admin',
  'org_admin',
  'rlevents_super_admin',
  'rlevents_tenant_admin',
];

/**
 * Computes which "areas" (top-level dashboards) the signed-in user can access.
 * Shared by the admin, staff, and coach/club headers so the area-switcher
 * dropdown stays consistent everywhere.
 *
 *  - Admin Dashboard  (/admin-dashboard): admin-type role OR org membership/ownership
 *  - Staff Portal     (/staff):           admin OR assigned (active) event staff
 *  - Club Dashboard   (/club-dash):       coach/club role OR has team registrations
 */
export function useAccessibleAreas() {
  const { user, profile } = useAuth();
  const [areas, setAreas] = useState<AccessibleArea[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!user?.id) {
      setAreas([]);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      setIsLoading(true);
      const uid = user.id;

      const [rolesRes, staffRes, orgRes, coachRegRes] = await Promise.all([
        supabase.from('user_roles').select('role').eq('user_id', uid),
        supabase
          .from('event_staff')
          .select('id')
          .eq('user_id', uid)
          .eq('is_active', true)
          .limit(1),
        supabase
          .from('organization_members')
          .select('id')
          .eq('user_id', uid)
          .limit(1),
        supabase
          .from('event_registrations_v2')
          .select('id')
          .eq('coach_user_id', uid)
          .limit(1),
      ]);

      const roles = (rolesRes.data || []).map((r: any) => r.role);
      const profileRole = profile?.role;

      const hasAdminRole =
        roles.some((r: string) => ADMIN_ROLES.includes(r)) ||
        (profileRole && ADMIN_ROLES.includes(profileRole));
      const hasOrg = (orgRes.data || []).length > 0;
      const isStaff = (staffRes.data || []).length > 0;
      const isCoach =
        (coachRegRes.data || []).length > 0 ||
        roles.includes('club_coach') ||
        roles.includes('coach') ||
        profileRole === 'club_coach';

      const result: AccessibleArea[] = [];
      if (hasAdminRole || hasOrg)
        result.push({ label: 'Admin Dashboard', path: '/admin-dashboard' });
      if (hasAdminRole || isStaff)
        result.push({ label: 'Staff Portal Dashboard', path: '/staff' });
      if (isCoach) result.push({ label: 'Club Dashboard', path: '/club-dash' });
      // Hotel Partner Portal shows for anyone holding the hotel_partner role,
      // including admins who also manage hotels.
      if (roles.includes('hotel_partner'))
        result.push({ label: 'Hotel Partner Portal', path: '/hotel-portal' });

      if (!cancelled) {
        setAreas(result);
        setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.id, profile?.role]);

  return { areas, isLoading };
}
